import { useEffect, useState } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
} from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import moment from "moment";
import { useAuth } from "../Context/AuthContext";
import "../Styling/AllTables.css";

import {
  getHolidaysByOrgId,
  createHoliday,
  updateHoliday,
} from "../Api/HolidayMasterApi";

const HolidayMasterOrg = () => {
  const { user } = useAuth();
  console.log("Logged-in user: ", user);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingHolidayId, setEditingHolidayId] = useState(null);

  const [holidays, setHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const ALLOWED_ROLES_FOR_ADD = ["HR", "Admin"];

  const canAddHoliday = user?.roles?.some((role) =>
    ALLOWED_ROLES_FOR_ADD.includes(role),
  );

  // 🔹 Form state
  const [formData, setFormData] = useState({
    holidayName: "",
    holidayDate: "",
    yearId: "",
    monthId: "",
    holidayType: "",
  });

  // =========================
  // Load Holidays
  // =========================
  const loadHolidays = async () => {
    try {
      const data = await getHolidaysByOrgId(user?.organisationId);
      setHolidays(data || []);
    } catch (error) {
      toast.error("Error fetching holidays");
    }
  };

  const MONTH_MAP = {
    "01": "January",
    "02": "February",
    "03": "March",
    "04": "April",
    "05": "May",
    "06": "June",
    "07": "July",
    "08": "August",
    "09": "September",
    10: "October",
    11: "November",
    12: "December",
  };

  const HOLIDAY_TYPE_OPTIONS = [
    { value: "", label: "Select Holiday Type" },
    { value: "National", label: "National" },
    { value: "Festival", label: "Festival" },
    { value: "Optional", label: "Optional" },
    { value: "Company", label: "Company" },
    { value: "Weekend", label: "Weekend" },
  ];

  useEffect(() => {
    if (user?.organisationId) {
      loadHolidays();
    }
  }, [user?.organisationId]);

  // =========================
  // Add Holiday
  // =========================
  const handleSaveHoliday = async () => {
    if (!formData.holidayName || !formData.holidayType) {
      toast.error("Holiday name and type are required");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        orgId: String(user.organisationId),
        holidayDate: formData.holidayDate || null,
        ...(isEditMode && { holidayId: editingHolidayId }),
      };

      if (isEditMode) {
        await updateHoliday(payload, String(user?.userId));
        toast.success("Holiday updated successfully");
      } else {
        await createHoliday(payload, String(user?.userId));
        toast.success("Holiday added successfully");
      }

      setShowModal(false);
      setIsEditMode(false);
      setEditingHolidayId(null);

      setFormData({
        holidayName: "",
        holidayDate: "",
        yearId: "",
        monthId: "",
        holidayType: "",
      });

      loadHolidays();
    } catch (error) {
      toast.error("Error saving holiday");
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (dateValue) => {
    if (!dateValue) {
      setFormData({
        ...formData,
        holidayDate: "",
        monthId: "",
        yearId: "",
      });
      return;
    }

    const selectedDate = new Date(dateValue);

    const monthNumber = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const year = selectedDate.getFullYear();

    setFormData({
      ...formData,
      holidayDate: dateValue,
      monthId: monthNumber, // ✅ STORE 01,02...
      yearId: String(year),
    });
  };

  const handleEditHoliday = (holiday) => {
    setIsEditMode(true);
    setEditingHolidayId(holiday.holidayId);

    setFormData({
      holidayName: holiday.holidayName,
      holidayDate: holiday.holidayDate
        ? moment(holiday.holidayDate).format("YYYY-MM-DD")
        : "",
      monthId: holiday.monthId || "",
      yearId: holiday.yearId || "",
      holidayType: holiday.holidayType,
    });

    setShowModal(true);
  };

  return (
    <div className="main-content expand">
      <Container fluid className="list-container">
        {/* ================= HEADER ================= */}
        <div className="header-background">
          <div className="heading-name">
            <h1 className="header-list-title">Holiday Master</h1>
          </div>

          {canAddHoliday && (
            <Button
              className="gradient-btn green-btn"
              onClick={() => setShowModal(true)}
            >
              <FaPlus style={{ marginRight: 8 }} />
              Add Holiday
            </Button>
          )}
        </div>

        {/* ================= TABLE ================= */}
        <div
          className="table-wrapper"
          style={{
            maxHeight: "80vh",
            overflow: "scroll",
          }}
        >
          <Table bordered hover className="data-table">
            <thead>
              <tr>
                <th>#</th>
                {/* <th>Holiday ID</th> */}
                <th>Holiday Name</th>
                <th>Date</th>
                {/* <th>Month</th> */}
                {/* <th>Year</th> */}
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {holidays.length > 0 ? (
                holidays.map((holiday, index) => (
                  <tr key={holiday.holidayId}>
                    <td>{index + 1}</td>
                    {/* <td>{holiday.holidayId}</td> */}
                    <td>{holiday.holidayName}</td>
                    <td>
                      {holiday.holidayDate
                        ? moment(holiday.holidayDate).format("DD-MMM-YYYY")
                        : "-"}
                    </td>
                    {/* <td>{MONTH_MAP[holiday.monthId] || "-"}</td> */}
                    {/* <td>{holiday.yearId}</td> */}
                    <td>{holiday.holidayType}</td>
                    <td style={{ display: "flex", justifyContent: "center" }}>
                      {canAddHoliday && (
                        <Button
                          size="sm"
                          variant="warning"
                          onClick={() => handleEditHoliday(holiday)}
                        >
                          Update
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    style={{ textAlign: "center", fontWeight: 600 }}
                  >
                    No holidays found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* ================= ADD HOLIDAY MODAL ================= */}
        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setIsEditMode(false);
            setEditingHolidayId(null);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {isEditMode ? "Update Holiday" : "Add Holiday"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-2">
                    <Form.Label>Holiday Name</Form.Label>
                    <Form.Control
                      value={formData.holidayName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          holidayName: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Holiday Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.holidayDate}
                      onChange={(e) => handleDateChange(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Holiday Type</Form.Label>
                    <Form.Select
                      value={formData.holidayType}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          holidayType: e.target.value,
                        })
                      }
                    >
                      {HOLIDAY_TYPE_OPTIONS.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Month</Form.Label>
                    <Form.Control
                      value={MONTH_MAP[formData.monthId] || ""}
                      disabled
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label>Year</Form.Label>
                    <Form.Control
                      value={formData.yearId}
                      onChange={(e) =>
                        setFormData({ ...formData, yearId: e.target.value })
                      }
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveHoliday} disabled={loading}>
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update"
                  : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default HolidayMasterOrg;
